import axios from '@/lib/axios';
import { useRouter } from 'next/navigation';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/auth';

interface RegisterFormInputs {
  name: string;
  email_or_phone: string;
  password: string;
  password_confirmation: string;
  register_by: string;
  otp_code: string;
  otp_signup: boolean;
}

const RegisterWithPass = () => {
  const { mutate } = useAuth();
  const router = useRouter();
  const [IsLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<RegisterFormInputs>({
    defaultValues: {
      register_by: 'email', // or 'phone' depending on user input
      otp_code: '',
      otp_signup: false,
    },
  });

  const onSubmit = async (data: RegisterFormInputs) => {
    setIsLoading(true);
    try {
      const payload = {
        ...data,
        otp_code: '',
        otp_signup: false,
      };

      const res = await axios.post('/api/v2/auth/signup', payload);

      if (res.data.result) {
        toast.success(res.data.message || 'Registration Successful.');
        mutate();
        router.push('/');
      } else {
        toast.error(res.data.message || 'Registration Failed.');
      }
    } catch (error: any) {
      if (error.response?.status === 422) {
        const apiErrors = error.response.data.errors;
        Object.entries(apiErrors).forEach(([field, messages]: any) => {
          setError(field as keyof RegisterFormInputs, {
            type: 'manual',
            message: messages[0],
          });
        });
      } else {
        console.error('Registration error:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const password = watch('password');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="row g-3">
        <div className="col-12">
          <label htmlFor="first-name" className="form-label">
            Full name
          </label>
          <input
            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
            type="text"
            id="first-name"
            placeholder="Enter Your Name"
            {...register('name', { required: 'Name is required' })}
          />
          {errors.name && (
            <div className="invalid-feedback">{errors.name.message}</div>
          )}
        </div>

        <div className="col-12">
          <label htmlFor="account-phone" className="form-label">
            Email or Phone
          </label>
          <input
            type="text"
            className={`form-control ${errors.email_or_phone ? 'is-invalid' : ''}`}
            id="account-phone"
            placeholder="Enter Your Email or Phone"
            {...register('email_or_phone', {
              required: 'Email or phone is required',
            })}
          />
          {errors.email_or_phone && (
            <div className="invalid-feedback">
              {errors.email_or_phone.message}
            </div>
          )}
        </div>

        <div className="col-12">
          <label htmlFor="account-pass" className="form-label">
            Password
          </label>
          <input
            type="password"
            className={`form-control ${errors.password ? 'is-invalid' : ''}`}
            id="account-pass"
            placeholder="Password"
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 8,
                message: 'Password must be at least 8 characters',
              },
            })}
          />
          <p>Must be at least 8 characters.</p>
          {errors.password && (
            <div className="invalid-feedback">{errors.password.message}</div>
          )}
        </div>

        <div className="col-12">
          <label htmlFor="account-cpass" className="form-label">
            Confirm Password
          </label>
          <input
            type="password"
            className={`form-control ${errors.password_confirmation ? 'is-invalid' : ''}`}
            id="account-cpass"
            placeholder="Re-type password"
            {...register('password_confirmation', {
              required: 'Please confirm your password',
              validate: (value) =>
                value === password || 'Passwords do not match',
            })}
          />
          {errors.password_confirmation && (
            <div className="invalid-feedback">
              {errors.password_confirmation.message}
            </div>
          )}
        </div>
      </div>

      <button
        disabled={IsLoading}
        type="submit"
        className="trk-btn trk-btn--secondary d-block mt-4"
      >
        {IsLoading ? 'Please wait...' : 'Register'}
      </button>
    </form>
  );
};

export default RegisterWithPass;
